import ResizeSensor from "css-element-queries/src/ResizeSensor";
import * as _ from "lodash";
import { action, observe, toJS } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import { NavLink } from "react-router-dom";

import { formatDateTime } from "../../shared/dateTimeUtils";
import config from "../../config";
import Chiclet from "../../shared/Chiclet/Chiclet";
import "../../shared/DiscretesAndTrends/DiscretesAndTrends.scss";
import TrendStore from "../../shared/DiscretesAndTrends/TrendStore";
import Loader from "../../shared/Loader/Loader";
import ZoomControl from "../../shared/ZoomControl";
import "./Monitor.scss";
import MonitorSuper from "./MonitorSuper";
import SubNav from "./SubNav";
import canvasWorkerUtils from "./canvasWorkerFactory";
import CanvasWorker from "./canvas.worker";
import { hasOffscreenCanvas } from "../../shared/performanceUtils";
import { DateTime } from "luxon";
import AhiChiclet from "../AHI/AhiChiclet";
import { AHI_REFRESH_DURATION } from "../AHI/constants";
import className from "../../shared/className";
import TimeAgo from "../../shared/TimeAgo";
import SideBar from "./components/Sidebar";
import DiscreteRow from "./components/DiscreteRow";
import TrendCharts from "./components/TrendCharts";
import { resetSnippet } from "./SnippetStore";

const LABEL_OFFSET_PX = 10;

export const MonitorContext = React.createContext();
class Monitor extends MonitorSuper {
  disposers = [];
  constructor(props) {
    super(props);
    resetSnippet(this.store);

    this.trendStore = new TrendStore({
      fetchTrends: this.store.fetchTrends,
      appStore: this.props.store,
    });
    if (!this.props.zoom) {
      this.store.pixelsPerSecond = this.store.initialPixelsPerSecond;
    }
    this.animateCharts = this.animateCharts.bind(this);
    this.wheel = this.wheel.bind(this);
    this.inertialScroll = this.inertialScroll.bind(this);
    this.updateTime = this.updateTime.bind(this);
    this.pauseLiveMode = this.pauseLiveMode.bind(this);
    this.refresh = this.refresh.bind(this);
    this.timeoutId = null;

    this.store.routeParams = this.props.match?.params;
    if (this.props.match?.params?.historyType?.match(/(history|event)/)) {
      this.store.liveMode = false;
    } else {
      this.store.liveMode = true;
    }
    if (this.store.liveMode) {
      this.store._measurements = undefined;
    }

    if (props.canvasWorker) {
      this.canvasWorker = props.canvasWorker;
    } else {
      this.constructCanvasWorker();
    }

    this.canvasWorker.postMessage({
      assignState: { liveMode: this.store.liveMode },
    });
    this.canvasWorker.addEventListener(
      "message",
      this.handleCanvasWorkerMessage,
    );

    this.disposers.push(
      observe(this.store, "liveMode", () => {
        this.canvasWorker.postMessage({
          assignState: { liveMode: this.store.liveMode },
        });
        if (this.store.liveMode) {
          props.measurementsSource?.resume();
        } else {
          props.measurementsSource?.pause();
        }
      }),
      observe(this.store, "pixelsPerSecond", () => {
        this.plotCharts({ pixelsPerSecond: this.store.pixelsPerSecond });
      }),
      observe(props.store, "prefs", () => {
        this.canvasWorker.postMessage({
          assignState: {
            sweep: props.store.prefs?.userPreferences?.client?.pm?.sweep,
          },
        });
      }),
    );
    if (this.props.setAlarm) {
      this.disposers.push(
        observe(this.store, "_measurements", () => {
          this.props.setAlarm(this.store.alarms?.[0]);
        }),
      );
    }

    if (props.measurementsSource) {
      this.snapToEnd = _.once(() => this.store.snapToEnd());
      props.measurementsSource.addEventListener("measurements", (event) => {
        if (!this.store.liveMode) return;
        this.store.setMeasurements(event.detail);
        try {
          const lastDateTime = DateTime.fromISO(
            event.detail.data.lastItem.timestamp,
          );
          const serverTime = lastDateTime?.plus({ seconds: 1 });
          if (serverTime?.isValid)
            this.store.updateLatency(DateTime.local().diff(serverTime));
        } catch (error) {
          return;
        }
        this.store.trimHistoryLive({ width: this.refCanvas?.width });
        this.snapToEnd();
        this.plotCharts();
      });
    }
  }

  render() {
    const alarms = this.store.getAlarms(this?.store?.chartEndTime);
    const highAlarmLevel = _.get(alarms[0], "level");
    if (
      this.props.inSplitScreen &&
      this.lastHighAlarmLevel !== highAlarmLevel &&
      this.el
    ) {
      this.lastHighAlarmLevel = highAlarmLevel;
      this.el
        .closest(".split-screen-item")
        .setAttribute("data-alarm-level", highAlarmLevel);
    }
    return (
      <MonitorContext.Provider value={this}>
        <div
          ref={(el) => (this.el = el)}
          {...className({
            "monitor widget": true,

            dragging: this.store.dragging,
            embedded: this.props.isEmbedded,
          })}
        >
          {this.props.liveOnly ? null : (
            <SubNav
              asmpid={this.asmpid}
              location={this.props.location}
              disable={{ selectTime: !this.store.enableSelectTime }}
              hideMainNav={this.props.store.hideMainNav}
            >
              <span>
                <NavLink
                  to={`/patient/${this.asmpid}/monitor/${this.historyType}`}
                  onClick={(e) => {
                    this.showHistorySelector(e).then(this.updateTime);
                  }}
                >
                  Select Time
                </NavLink>
                <a
                  href={`/patient/${this.asmpid}/monitor/live`}
                  onClick={this.toggleAnimation}
                  className={this.cn({
                    active: this.store.liveMode,
                    "pointer-override": true,
                  })}
                >
                  Live
                </a>
              </span>
            </SubNav>
          )}
          <div className="with-docked-list">
            {!(this.props.suppressRouting || config.pm.hideSideBar) && (
              <SideBar
                historyType={this.props.match?.params?.historyType}
                parent={this}
              />
            )}
            <div className="content-wrapper">
              <DiscreteRow
                parent={this}
                applyDiscreteSchemeColors={this.applyDiscreteSchemeColors}
              />
              <div className="monitor-body">
                <div className="charts">
                  <TrendCharts
                    trendStore={this.trendStore}
                    colorScheme={this.colorScheme}
                    updateTime={this.centerToTime}
                  />
                  {this.store.loading ? (
                    <Loader />
                  ) : (
                    <div
                      className={this.cn({
                        "monitor-charts": true,
                        loading: this.store.loadingMeasurements,
                        scan: this.scan,
                      })}
                      ref={(r) => (this.refMonitorCharts = r)}
                    >
                      {this.store.hasNoTele ? (
                        <div className="no-telem">No Tele Data</div>
                      ) : null}
                      <div className="monitor-events">
                        {alarms.map(
                          (alarm, i) =>
                            alarm?.message.length > 0 && (
                              <div
                                className={`event ${(
                                  alarm.level || ""
                                ).toLowerCase()}`}
                                key={i}
                              >
                                <div className="summary">
                                  <div className="message">{alarm.message}</div>
                                </div>
                              </div>
                            ),
                        )}
                      </div>
                      {this.store.chartEndTime ? (
                        <div
                          className="chart-end-time"
                          ref={(r) => (this.refTimestamp = r)}
                        >
                          {/* This is where the end time goes, but it's inserted by renderCharts. */}
                        </div>
                      ) : null}
                      {this.store.config && this.store.measurements
                        ? this.store.config.waveGroups.map((group, i) => {
                            return (
                              <div
                                {...this.className({
                                  "monitor-chart": 1,
                                  disabled: group.disabled,
                                })}
                                id={`chart-${group.key}`}
                                key={i}
                              >
                                <div
                                  className="label"
                                  onClick={() => this.toggleChart(group)}
                                  style={{
                                    top:
                                      this.store.chartHeight * i +
                                      LABEL_OFFSET_PX -
                                      this.store.offsetY +
                                      "px",
                                  }}
                                >
                                  {group.title}
                                </div>
                              </div>
                            );
                          })
                        : null}
                      <ZoomControl
                        zoom={this.store.zoom}
                        zoomChange={this.zoomChange}
                      />
                      <canvas
                        ref={(r) => (this.refCanvas = r)}
                        onMouseDown={this.canvasMouseDown}
                        onTouchStart={this.canvasMouseDown}
                      />
                      {!this.store.liveMode ? (
                        <TimeAgo
                          precision="seconds"
                          className="latency"
                          offsetX={this.store.offsetX}
                          endTime={this.store.chartEndTimeAdjusted}
                        />
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="discrete-column color-scheme-dark">
                  {!this.store.loading &&
                    this.props.verticalChicletsOnly &&
                    this.getChiclets()}
                  {this.store.discreteMeasurements
                    .map(this.applyDiscreteSchemeColors)
                    .map((discrete) => (
                      <Chiclet
                        chart={discrete}
                        toggleChart={() =>
                          this.props.disableTrends ||
                          this.trendStore.toggleTrendChart(
                            discrete,
                            this.store.chartEndTime,
                          )
                        }
                        key={this.trendStore.discreteKey(discrete)}
                        active={this.trendStore.isDiscreteActive(discrete)}
                        showTrends={config.pm.showTrends}
                      />
                    ))}
                </div>
              </div>
              {/* /.monitor-body */}
            </div>
          </div>
        </div>
      </MonitorContext.Provider>
    );
  }

  /***********  React lifecycle methods ***********************/
  componentDidMount() {
    this.setCanvasSize();
    super.componentDidMount();
    this.resetOnWake();
    this.store.isEmbedded = this.props.isEmbedded;
    // start the animation, also updates time ago counter.
    requestAnimationFrame(this.animateCharts);

    if (
      this.props.match?.params?.historyType === "history" &&
      !this?.props?.match?.params?.timestamp
    ) {
      // This is triggered by choosing "Select Time" from Monitor Events.
      this.showHistorySelector()
        .then(this.updateTime)
        .catch((err) => console.error("showHistorySelector error", err));
    }

    if (config.pm.pauseOnBlur) {
      window.addEventListener("blur", this.pauseLiveMode);
      window.addEventListener("focus", this.pauseLiveMode);
      window.addEventListener("IEDControl_ActiveChanged", this.pauseLiveMode);
    }

    if (config.pm.preventTimeout) {
      this.props.store.authStore.blockTimeout(this);
    }
    this.startPolling();
    if (config.pm.canvasWorker) {
      this.canvasWorker.postMessage(config.pm.canvasWorker);
    }

    if (config.ahi.enabled) {
      this.store.fetchAhiData();
      this.store.fetchAhiDescriptions();
      this.ahiRefreshInterval = setInterval(
        () => this.store.fetchAhiData(),
        AHI_REFRESH_DURATION,
      );
    }
  }
  componentDidUpdate(prevProps) {
    super.componentDidUpdate();
    if (!this.store.loadingMeasurements) {
      this.store.hasNoTele = this.hasNoTele();
    }
    this.zoomToFit();
    this.store.isEmbedded = this.props.isEmbedded;
    this.transferCanvas(this.refCanvas);
    if (!this.resizeSensor && this.refCanvas) {
      this.resizeSensor = new ResizeSensor(
        this.refMonitorCharts,
        this.setCanvasSize,
      );
    }
    this.setCanvasSize();
    if (this.props.liveOnly && !prevProps.liveOnly) {
      this.toggleAnimation(null, true);
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.animateCharts = _.noop;

    this.props.store.authStore.unblockTimeout(this);
    clearInterval(this.bufferRefreshInterval);
    clearInterval(this.canvasWorkerSync);
    clearInterval(this.canvasWorkerSyncInterval);
    clearInterval(this.ahiRefreshInterval);
    window.removeEventListener("blur", this.pauseLiveMode);
    window.removeEventListener("focus", this.pauseLiveMode);
    this.canvasWorker.postMessage({ dropInstance: true });
    this.canvasWorker.terminate();
    this.disposers.forEach((disposer) => disposer());
  }
  /*********** / React lifecycle methods ***********************/

  // this refresh is triggered by going to the same location, ie.: clicking 'Monitor' top nav.
  refresh() {
    this.store._measurements = undefined;
    this.store.getMonitorConfig({ suppressLoading: true }).then(() => {
      this.store.liveMode = true;
      this.startLiveMode();
    });
  }

  /***********  getters ***********************/

  get chartStartTime() {
    return this.store.chartEndTime?.minus({
      seconds: this.chartLengthSeconds || 0,
    });
  }

  get shouldZoomToFit() {
    return (
      !this.props.zoom &&
      !this.waveformsFitted &&
      this.refCanvas &&
      this.props.fitWaveforms &&
      _.get(this.store.config, "waveGroups.length")
    );
  }
  get chartLengthSeconds() {
    const width =
      this.canvasSize?.width ||
      this.refMonitorCharts?.clientWidth ||
      this.el?.clientWidth;
    return width / this.store.pixelsPerSecond;
  }
  get slicedWaveforms() {
    return this.store.sliceWaveforms({
      chartStartTime: this.chartStartTime,
    });
  }
  get sweepMode() {
    return this.props.store.prefs?.userPreferences?.client?.pm?.sweep;
  }
  get historyType() {
    return this.props.match?.params?.historyType || "history";
  }

  /******************* DOM Event handlers ***********/

  handleCanvasWorkerMessage = (e) => {
    if (e.data?.plotCharts) {
      this.plotting = false;
      if (this.plotMessage) {
        this.canvasWorker.postMessage(this.plotMessage);
        this.plotMessage = null;
      }
    }
  };
  /******************* / DOM Event handlers ***********/

  zoomToFit() {
    if (!this.shouldZoomToFit) return;
    this.waveformsFitted = true;
    const { offsetHeight } = this.refMonitorCharts;
    while (
      this.store.zoom > (config.pm.zoom?.min || 1) &&
      this.store.config.waveGroups.length * this.store.chartHeight >
        offsetHeight
    ) {
      this.store.zoom = this.store.zoom - 0.01;
    }
    this.store.offsetY = this.store.defaultOffsetY;
  }
  transferCanvas() {
    if (this.refCanvas) {
      this.canvas = hasOffscreenCanvas
        ? this.refCanvas.transferControlToOffscreen()
        : this.refCanvas;
      this.initCanvasWorker();
      this.transferCanvas = () => {};
    }
  }

  initCanvasWorker() {
    this.canvasWorker.postMessage(
      {
        init: {
          canvas: this.canvas,
          visible: true,
          waveGroups: toJS(this.store.config?.waveGroups) || [],
          pixelsPerSecond: this.store.pixelsPerSecond,
          sweep: this.sweepMode,
          sweepHideDot: config.pm?.sweepHideDot,
        },
      },
      [this.canvas],
    );
  }
  clearCanvas() {
    this.canvasWorker.postMessage({ clearCanvas: true });
  }

  setCanvasSize = () => {
    requestAnimationFrame(() => {
      const canvas = this.refCanvas;
      if (!canvas) return;
      const { width, height, offsetWidth, offsetHeight } = canvas;
      if (
        width !== Math.round(offsetWidth) ||
        height !== Math.round(offsetHeight)
      ) {
        this.canvasSize = { width: offsetWidth, height: offsetHeight };
        this.canvasWorker.postMessage({
          setCanvasSize: this.canvasSize,
        });
        this.plotCharts();
      }
    });
  };
  paintCanvas() {
    const canvas = this.refCanvas;
    if (!canvas) {
      return;
    }
    if (this.store.config) {
      this.limitVerticalScroll();
    }
  }

  updateTimestamp = (chartEndTime = this.store.chartEndTime) => {
    if (!this.refTimestamp) return;
    let time = chartEndTime;
    if (this.store.liveMode)
      time = this.store.serverTime?.minus({
        seconds: this.store.liveModeDelaySeconds,
      });
    if (!time?.isValid)
      time = DateTime.fromISO(this.props.match.params.timestamp);
    if (!time?.isValid)
      time = DateTime.now().minus({ seconds: this.store.liveModeDelaySeconds });
    if (!time?.isValid) time = DateTime.now();
    this.refTimestamp.innerText = formatDateTime(time);
  };

  plotCharts = ({ data, pixelsPerSecond } = {}) => {
    const canvas = this.refCanvas;
    if (!canvas) return;

    if (this.store.config) {
      if (this.store.liveMode) {
        this.store.offsetX = 0;
      } else if (this.store.offsetX) {
        this.store.chartEndTime = this.store.originalChartEndTime?.plus({
          seconds: this.store.offsetX / this.store.pixelsPerSecond,
        });
      }
      this.updateTimestamp();

      if (!this.store.hasData()) return;
      const slicedWaveforms =
        data?.data ||
        this.slicedWaveforms
          .filter((s) => s.waveData.length) // This is where blank data is filtered out. The canvasWorker will render a gap when it finds non-consecutive seconds, so there no reason to feed it the blank data, whether real or fake.
          .map((s) => ({
            timestamp: s.timestamp,
            waveData: toJS(s.waveData),
          }));

      const {
        chartHeight,
        offsetY,
        chartEndTime,
        liveMode,
        liveModeDelaySeconds,
        serverTime,
      } = this.store;

      // Render each waveform.
      const canvasWorkerMessage = {
        plotCharts: {
          pixelsPerSecond: this.store.pixelsPerSecond,
          slicedWaveforms,
          chartHeight,
          offsetY,
          chartEndTime: liveMode
            ? serverTime.minus({ seconds: liveModeDelaySeconds }).toISO()
            : chartEndTime?.toISO(),
        },
      };

      if (
        !pixelsPerSecond &&
        this.plotting &&
        this.store.liveMode &&
        performance.now() - this.plotting < 300
      ) {
        this.plotMessage = canvasWorkerMessage;
      } else {
        this.plotMessage = null;
        this.plotting = performance.now();
        this.canvasWorker.postMessage(canvasWorkerMessage);
      }

      // TODO: use _.once
      if (!this.store.enableSelectTime && !this.enableSelectTimeTimeout) {
        this.enableSelectTimeTimeout = setTimeout(() => {
          this.store.enableSelectTime = true;
        }, 200);
      }
    }
  };

  limitVerticalScroll() {
    // Limit vertical scrolling to at least keep at least one waveform on the canvas.
    if (!this.refCanvas || this.refCanvas.height === 1) return;
    this.store.offsetY = _.clamp(
      this.store.offsetY,
      Math.min(
        this.store.defaultOffsetY,
        this.store.chartHeight - this.refCanvas.height,
      ),
      Math.max(
        this.store.defaultOffsetY,
        this.store.chartHeight * (this.store.config.waveGroups.length - 1),
      ),
    );
  }

  toggleAnimation = action((e, liveMode = !this.store.liveMode) => {
    e && e.preventDefault();

    if (e && e.shiftKey) {
      this.scan = !this.scan;
      return;
    }

    if (liveMode) {
      // start the scrolling animation
      this.startLiveMode();
      this.props.history.replace(
        this.props.history.location.pathname.replace(
          /(history|event|event-history)\/.*/,
          "",
        ),
      );
      this.eventTarget.dispatchEvent(
        new CustomEvent("liveModeStart", {
          details: {},
        }),
      );
    } else {
      e && e.preventDefault();
      this.store.liveMode = false;
      if (!this.props.suppressRouting) {
        this.props.history.replace(
          this.props.history.location.pathname.replace(
            /monitor.*/,
            `monitor/history/${this.store.chartEndTime.toISO()}`,
          ),
        );
      }
      this.store.animationStartTime = undefined;
    }
  });
  startLiveMode() {
    this.props.history.replace(
      this.props.history.location.pathname.replace(/(history|event)\/.*/, ""),
    );
    this.store._measurements = undefined;
    this.canvasWorker.postMessage({
      clearCanvas: true,
    });
    if (this.chartLengthSeconds) {
      this.store
        .startLiveMode({
          seconds: this.chartLengthSeconds + this.store.liveModeDelaySeconds,
        })
        .then(this.animateCharts);
    }

    const intervalId = setInterval(() => {
      if (!this.liveMode) {
        clearInterval(intervalId);
        return;
      }
      this.updateTimestamp(this.store.chartEndTime);
    }, 1000);
    this.disposers.push(() => clearInterval(intervalId));
  }
  startPolling() {
    this.stopPolling();
    this.bufferRefreshInterval = setInterval(() => {
      if (this.store.loadingMeasurements) return;
      if (this.props.isEmbedded && this.store.liveMode) {
        return;
      }
      if (this.refCanvas) {
        if (this.store.config) {
          this.trimHistory();
        }
        this.store.chartWidth = this.refCanvas.width;
        this.store
          .updateMeasurementBuffers({
            pixelWidth: this.refCanvas.width,
          })
          ?.then?.(() => {
            this.plotCharts();
          })
          .catch((e) => console.error("updateMeasurementBuffers failed", e));
      }
    }, 1000);
  }
  stopPolling() {
    clearInterval(this.bufferRefreshInterval);
  }

  animateCharts = () => {
    if (this.store.liveMode) {
      this.animateCharts.animationFrame = requestAnimationFrame(
        this.animateCharts,
      );

      const pNow = performance.now();
      let { animationStartTime, originalChartEndTime } = this.store;
      animationStartTime = !animationStartTime
        ? performance.now()
        : this.store.animationStartTime;

      this.store.chartEndTime = originalChartEndTime?.plus({
        milliseconds: pNow - animationStartTime,
      });

      this.paintCanvas();
    }
  };

  /**
   *
   * @param {DateTime} time
   */
  centerToTime = (time) => {
    if (time) {
      const endTime = time.plus({
        seconds: this.chartLengthSeconds / 2,
      });
      this.updateTime(endTime);
    }
  };

  /**
   *
   * @param {DateTime} time
   * @returns
   */
  updateTime(time) {
    let endTime;
    if (!time) {
      return;
    }

    endTime = time;

    this.props.history.push(
      this.props.location.pathname.replace(
        /(.*\/monitor)(.*)/,
        `$1/${this.historyType}/${endTime.toISO()}`,
      ),
    );
    this.store.chartEndTime = this.store.originalChartEndTime = endTime;
    this.store.offsetX = 0;
    this.store.liveMode = false;
    cancelAnimationFrame(this.animateCharts.animationFrame);
    this.stopPolling();
    this.store.resetMeasurements();
    const starttime = this.chartStartTime;

    this.store
      .fetchMonitorMeasurements(
        {
          endtime: endTime.toISO(),
          starttime: starttime.toISO(),
        },
        true,
      )
      .then(() => {
        this.store.chartEndTime = endTime;
        this.store.liveMode = false;
        this.updateTimestamp();
        this.startPolling();
      });

    this.store.timeSnapshot = DateTime.now();
  }

  trimHistory() {
    this.store.trimHistory(this.chartLengthSeconds);
  }

  toggleChart = action((group) => {
    group.disabled = !group.disabled;
    const { key, disabled } = group;
    this.canvasWorker.postMessage({ setWaveDisabled: { key, disabled } });
    this.plotCharts();
  });

  renderCharts() {
    this.plotCharts();
  }
  pauseLiveMode(e) {
    // This is used to sleep/wake when switching tabs.
    if (e && e.type === "blur" && this.store.liveMode) {
      this.store.liveMode = false;
      this.pausedLiveMode = true;
    } else if (e && e.type === "focus" && this.pausedLiveMode) {
      this.store.liveMode = true;
      this.startLiveMode();
      this.pausedLiveMode = false;
    }
  }

  hasNoTele() {
    const slicedWaveforms = this.slicedWaveforms;
    const inFetchedTimeRange = this.store.inFetchedTimeRange({
      start: this.chartStartTime,
    });
    return (
      inFetchedTimeRange && !slicedWaveforms.find((w) => w.waveData.length)
    );
  }

  constructCanvasWorker() {
    if (this.props.canvasWorker) {
      this.canvasWorker = this.props.canvasWorker;
    } else if (hasOffscreenCanvas) {
      this.canvasWorker = new CanvasWorker();
    } else {
      const canvasWorkerInstance = new canvasWorkerUtils();

      this.canvasWorker = {
        addEventListener: () => {},
        terminate: () => {
          canvasWorkerInstance.destroy();
        },
        postMessage: (payload) => {
          Object.entries(payload).forEach(([method, arg]) => {
            if (canvasWorkerInstance[method]) canvasWorkerInstance[method](arg);
            else console.error("Method not found", method, arg);
          });
        },
      };
    }

    if (this.store.liveMode)
      this.canvasWorker.postMessage({ assignState: { liveMode: true } });
  }
  /**
   * Prevents scrolling into the future
   * @param {number} deltaX - pixel movement
   * @returns {boolean} true if you've reached the end.
   */
  panToLiveMode(deltaX) {
    const now = DateTime.now();
    const newTime = this.store.chartEndTime.minus({
      seconds: -deltaX / this.store.pixelsPerSecond || 0,
    });
    const maxTime = now
      .minus(this.store.serverLatency || 0)
      .minus({ seconds: this.store.liveModeDelaySeconds });
    if (newTime >= maxTime && deltaX < 0) {
      this.store.chartEndTime = maxTime;
      this.store.offsetX = 0;
      this.store.dragEvent = undefined;
      this.endDrag();
      if (!this.sweepMode) {
        // drop the drag
        this.toggleAnimation?.(null, true);
        return true;
      }
    }
    return false;
  }

  getChiclets() {
    return (
      <>
        {config.ahi.enabled && (
          <AhiChiclet
            data={this.store.ahiData}
            ahiDescriptions={this.store.ahiDescriptions}
            disabled={!this.store.liveMode || !this.store.ahiData}
          />
        )}
        {this.store.discretesRow &&
          this.store.discretesRow
            .map(this.applyDiscreteSchemeColors)
            .map((discrete) => (
              <Chiclet
                chart={discrete}
                toggleChart={() =>
                  !this.props.disableTrends &&
                  this.trendStore.toggleTrendChart(
                    discrete,
                    this.store.chartEndTime,
                  )
                }
                key={this.trendStore.discreteKey(discrete)}
                active={this.trendStore.isDiscreteActive(discrete)}
                showTrends={config.pm.showTrends}
              />
            ))}
      </>
    );
  }

  applyDiscreteSchemeColors = (discrete) => {
    return {
      ...discrete,
      color: discrete.schemeColors[this.colorScheme],
    };
  };

  get colorScheme() {
    return config.FF_DARK_MODE ? this.props.store.colorScheme : "darkMode";
  }

  resetOnWake() {
    // We don't need to bind visibilitychange when it's in MPV mode, because the Surveil component will handle it.
    if (this.props.measurementsSource) return;
    const listener = () => {
      const visible = !document.hidden;
      if (visible) {
        this.wake();
      } else {
        this.sleep();
      }
    };
    document.addEventListener("visibilitychange", listener);
    this.disposers.push(() =>
      document.removeEventListener("visibilitychange", listener),
    );
  }
  sleep() {
    if (this.store.liveMode) {
      this.store.resetMeasurements();
      this.canvasWorker.postMessage({ resetLiveMode: true });
      this.store.liveMode = false;
      this.wake = () => {
        // the animation frame to clear the canvas won't run when the window is hidden.
        // so we call it again on wake to make sure the canvas clears.
        this.canvasWorker.postMessage({ resetLiveMode: true });
        this.store.liveMode = true;
        this.startLiveMode();
        this.wake = () => {};
      };
    }
  }
  wake() {
    // stub
  }
}

export default inject("store")(observer(Monitor));
